import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import i18next from 'i18next';

/**
 * Fängt einen Absturz beim Zeichnen ab.
 *
 * Ohne das zeigt React nach einem Fehler einen **weißen Bildschirm** — für den
 * Nutzer nicht zu unterscheiden von „alles weg". Dabei liegt jeder Eintrag
 * sicher in der Datenbank auf dem Gerät; das soll die Seite auch sagen.
 *
 * Zwei Ebenen: um die ganze App (`scope="app"`) und um den Inhalt des Reiters
 * (`scope="screen"`). Ein kaputter Reiter lässt so die Reiterleiste stehen; ein
 * Wechsel des Reiters setzt die Grenze zurück (über `key`).
 */
export class ErrorBoundary extends Component<
  { scope: 'app' | 'screen'; children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sichtbar machen, nicht verschlucken — in der Konsole mit Bauteilpfad.
    console.error('Absturz in der Oberfläche:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const t = i18next.t.bind(i18next);
    const screen = this.props.scope === 'screen';

    return (
      <div
        role="alert"
        className={
          screen
            ? 'kw-tile flex flex-col gap-3 border-danger'
            : 'mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center gap-4 px-4'
        }
      >
        <h2 className="font-display text-xl font-bold uppercase text-danger">
          {t(screen ? 'crash.screenHeading' : 'crash.heading')}
        </h2>
        <p className="text-sm">{t('crash.safe')}</p>
        <button type="button" onClick={() => window.location.reload()} className="kw-btn">
          {t('crash.reload')}
        </button>
        <details className="text-xs text-ink-muted">
          <summary className="cursor-pointer">{t('crash.details')}</summary>
          <pre className="mt-2 whitespace-pre-wrap break-all">{error.message}</pre>
        </details>
      </div>
    );
  }
}
